import { create } from 'zustand';
import { ChuteStatus } from './sortingSettings';

export type DirectionId = number;
export type ChuteId = number;

export type Direction = {
  id: DirectionId;
  name: string;
};

export type Chute = {
  id: ChuteId;
  name: string;
  isDefault: boolean;
  status: ChuteStatus;
};

export type Assignments = Record<ChuteId, DirectionId[]>;

export type ChuteSettingsSnapshot = {
  directions: Direction[];
  chutes: Chute[];
  assignments: Assignments;
  version?: string;
};

type PendingConfirm =
  | { kind: 'remove'; chuteId: ChuteId; directionId: DirectionId }
  | { kind: 'clearChute'; chuteId: ChuteId }
  | { kind: 'resetAll' };

function uniqSorted(ids: number[]): number[] {
  return Array.from(new Set(ids));
}

function normalizeAssignments(input: Assignments, chutes: Chute[]): Assignments {
  const out: Assignments = {};
  for (const chute of chutes) {
    const ids = input[chute.id] ?? [];
    out[chute.id] = uniqSorted(ids);
  }
  return out;
}

type State = {
  snapshot: ChuteSettingsSnapshot | null;
  currentAssignments: Assignments;

  search: string;

  isLoading: boolean;
  error: string | null;

  pendingConfirm: PendingConfirm | null;

  // derived helpers
  canEditChute: (chuteId: ChuteId) => boolean;
  getStatus: (chuteId: ChuteId) => ChuteStatus | undefined;
  setChuteStatus: (chuteId: ChuteId, status: ChuteStatus) => void;

  // wiring

  // load/save
  load: (snapshot: ChuteSettingsSnapshot) => void;

  // search
  setSearch: (value: string) => void;

  // mutations
  assign: (chuteId: ChuteId, directionId: DirectionId) => void;
  requestRemove: (chuteId: ChuteId, directionId: DirectionId) => void;

  requestClearChute: (chuteId: ChuteId) => void;

  requestResetAll: () => void;
  resetAllConfirmed: () => void;

  unassignSilent: (chuteId: ChuteId, directionId: DirectionId) => void;
  assignSilent: (chuteId: ChuteId, directionId: DirectionId) => void;

  // modal
  closeConfirm: () => void;
};

export const useChuteSettingsStore = create<State>((set, get) => ({
  snapshot: null,
  currentAssignments: {},

  search: '',

  isLoading: false,
  error: null,

  pendingConfirm: null,

  setChuteStatus: (chuteId, status) => {
    set((s) => {
      if (!s.snapshot) return s;

      const idx = s.snapshot.chutes.findIndex((c) => c.id === chuteId);
      if (idx === -1) return s;

      const current = s.snapshot.chutes[idx];
      if (current.status === status) return s;

      const nextChutes = [...s.snapshot.chutes];
      nextChutes[idx] = { ...current, status };

      return {
        ...s,
        snapshot: {
          ...s.snapshot,
          chutes: nextChutes,
        },
      };
    });
  },

  getStatus: (chuteId) => {
    const snap = get().snapshot;
    const chute = snap?.chutes.find((c) => c.id === chuteId);
    return chute?.status;
  },

  canEditChute: (chuteId) => {
    const snap = get().snapshot;
    const chute = snap?.chutes.find((c) => c.id === chuteId);
    return Boolean(chute && !chute.isDefault);
  },

  assignSilent: (chuteId, directionId) => {
    const snap = get().snapshot;
    if (!snap) return;
    if (!get().canEditChute(chuteId)) return;

    set((s) => {
      const prev = s.currentAssignments[chuteId] ?? [];
      if (prev.includes(directionId)) return s;
      const next = uniqSorted([...prev, directionId]);
      return { ...s, currentAssignments: { ...s.currentAssignments, [chuteId]: next } };
    });
  },

  unassignSilent: (chuteId, directionId) => {
    const snap = get().snapshot;
    if (!snap) return;
    if (!get().canEditChute(chuteId)) return;

    set((s) => {
      const prev = s.currentAssignments[chuteId] ?? [];
      const next = prev.filter((id) => id !== directionId);
      return { ...s, currentAssignments: { ...s.currentAssignments, [chuteId]: next } };
    });
  },

  load: (snapshot) => {
    const normalized = normalizeAssignments(snapshot.assignments, snapshot.chutes);
    set({
      snapshot,
      currentAssignments: normalized,
      error: null,
      isLoading: false,
      pendingConfirm: null,
    });
  },

  setSearch: (value) => set({ search: value }),

  assign: (chuteId, directionId) => {
    const snap = get().snapshot;
    if (!snap) return;
    if (!get().canEditChute(chuteId)) return;

    set((s) => {
      const prev = s.currentAssignments[chuteId] ?? [];
      if (prev.includes(directionId)) return s;
      const next = uniqSorted([...prev, directionId]);
      return {
        ...s,
        currentAssignments: { ...s.currentAssignments, [chuteId]: next },
      };
    });
  },

  requestRemove: (chuteId, directionId) => {
    if (!get().canEditChute(chuteId)) return;
    set({ pendingConfirm: { kind: 'remove', chuteId, directionId } });
  },

  requestClearChute: (chuteId) => {
    if (!get().canEditChute(chuteId)) return;
    set({ pendingConfirm: { kind: 'clearChute', chuteId } });
  },

  requestResetAll: () => set({ pendingConfirm: { kind: 'resetAll' } }),

  resetAllConfirmed: () => {
    const snap = get().snapshot;
    if (!snap) {
      set({ pendingConfirm: null });
      return;
    }

    set((s) => {
      const next: Assignments = { ...s.currentAssignments };
      for (const chute of snap.chutes) {
        if (chute.isDefault) continue;
        next[chute.id] = [];
      }
      return { ...s, currentAssignments: next, pendingConfirm: null };
    });
  },

  closeConfirm: () => set({ pendingConfirm: null }),
}));
